import { create } from 'zustand';
import { contactsApi } from '../api/contacts.api.js';

function sortContacts(contacts) {
  return [...contacts].sort((a, b) => {
    const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : new Date(a.createdAt).getTime();
    const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : new Date(b.createdAt).getTime();
    return bTime - aTime;
  });
}

export const useContactStore = create((set, get) => ({
  contacts: [],
  selectedContact: null,
  isLoading: false,
  filter: 'open',  // open | unread | mine | unassigned | snoozed | closed
  channel: 'ALL',  // ALL | SMS | WHATSAPP | EMAIL
  search: '',
  meta: null,

  setFilter: (filter) => set({ filter }),
  setChannel: (channel) => set({ channel }),
  setSearch: (search) => set({ search }),

  selectContact: (contact) => set({ selectedContact: contact }),

  fetchContacts: async (params = {}) => {
    set({ isLoading: true });
    try {
      const { data } = await contactsApi.list({ limit: 300, ...params });
      set({ contacts: sortContacts(data.data), meta: data.meta });
    } finally {
      set({ isLoading: false });
    }
  },

  updateContact: (updatedContact) => {
    set((state) => {
      const exists = state.contacts.find((c) => c.id === updatedContact.id);
      let contacts;

      if (exists) {
        contacts = state.contacts.map((c) =>
          c.id === updatedContact.id ? { ...c, ...updatedContact } : c
        );
      } else {
        // New contact (auto-created from inbound) — add to list
        contacts = [...state.contacts, updatedContact];
      }

      return {
        contacts: sortContacts(contacts),
        selectedContact:
          state.selectedContact?.id === updatedContact.id
            ? { ...state.selectedContact, ...updatedContact }
            : state.selectedContact,
      };
    });
  },

  // Called when a new message arrives — updates lastMessageAt + preview + re-sorts immediately
  touchContactFromMessage: (message) => {
    set((state) => {
      const contacts = state.contacts.map((c) =>
        c.id === message.contactId
          ? {
              ...c,
              lastMessageAt: message.createdAt,
              _lastMessagePreview: message.content,
              _lastMessageChannel: message.channel,
              _lastMessageDirection: message.direction,
              // The server reopens a conversation on any inbound message; mirror it locally.
              ...(message.direction === 'INBOUND' && { conversationStatus: 'OPEN', snoozedUntil: null }),
            }
          : c
      );
      return { contacts: sortContacts(contacts) };
    });
  },

  incrementUnread: (contactId) => {
    set((state) => ({
      contacts: state.contacts.map((c) =>
        c.id === contactId
          ? { ...c, _count: { ...c._count, messages: (c._count?.messages || 0) + 1 } }
          : c
      ),
    }));
  },

  clearUnread: (contactId) => {
    set((state) => ({
      contacts: state.contacts.map((c) =>
        c.id === contactId ? { ...c, _count: { ...c._count, messages: 0 } } : c
      ),
      selectedContact:
        state.selectedContact?.id === contactId
          ? { ...state.selectedContact, _count: { ...state.selectedContact._count, messages: 0 } }
          : state.selectedContact,
    }));
  },

  setUnreadCount: (contactId, unreadCount) => {
    set((state) => ({
      contacts: state.contacts.map((c) =>
        c.id === contactId ? { ...c, _count: { ...c._count, messages: unreadCount } } : c
      ),
      selectedContact:
        state.selectedContact?.id === contactId
          ? { ...state.selectedContact, _count: { ...state.selectedContact._count, messages: unreadCount } }
          : state.selectedContact,
    }));
  },

  clearAllUnread: () => {
    set((state) => ({
      contacts: state.contacts.map((c) => ({ ...c, _count: { ...c._count, messages: 0 } })),
      selectedContact: state.selectedContact
        ? { ...state.selectedContact, _count: { ...state.selectedContact._count, messages: 0 } }
        : state.selectedContact,
    }));
  },

  // Total unread across all contacts
  totalUnread: () => {
    return get().contacts.reduce((sum, c) => sum + (c._count?.messages || 0), 0);
  },
}));
